import { forwardRef } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import './primitives.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, size = 'md', placeholder, className, value, ...rest },
  ref,
) {
  const classes = ['lm-select', size !== 'md' ? `lm-select--${size}` : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <select ref={ref} className={classes} value={value} {...rest}>
      {placeholder !== undefined && (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  );
});

export interface SelectWrapProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children: ReactNode;
}

/* Native selects can't render an SVG arrow; overlay the shared lucide chevron
   so every dropdown uses the same glyph. Wraps a caller-styled <select>. */
export function SelectWrap({ size = 'md', className, children }: SelectWrapProps) {
  const classes = ['lm-select-wrap', size !== 'md' ? `lm-select-wrap--${size}` : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {children}
      <ChevronDown size={14} className="lm-select-wrap__chevron" aria-hidden="true" />
    </div>
  );
}
